import {RefObject, useCallback, useEffect, useState, useRef} from 'react';
import {useLivenessStore} from '../store/useLivenessStore';
import {WebcamRefTypes} from 'components/Webcam';
import {useProgressStore} from 'components/LivenessDetection/store';
import {usePreloadLivenessModels} from './usePreloadLivenessModels';
import {type Keypoint} from '@tensorflow-models/face-landmarks-detection';
import {throttle} from 'utils/throttle';
import {useSyncRef} from '@guestapp/core';

const LEFT_THRESHOLD = 1.2;
const RIGHT_THRESHOLD = -1.2;
const UP_THRESHOLD = 0.3;
const NORMAL_OFFSET_Y = 1.2;
const EAR_THRESHOLD = 0.18;

const FACE_POSITION_THROTTLE = 150;
const PROGRESS_THROTTLE = 150;
const BLINK_CHECK_THROTTLE = 75;
const DETECTION_FRAME_SKIP = 1;

const calculateEAR = (landmarks: Keypoint[], eyeIndices: number[]) => {
  const [p1, p2, p3, p4] = eyeIndices.map(idx => landmarks[idx]);
  const vertical = Math.abs(p2.y - p3.y); // Vertical distance (upper and lower eyelids)
  const horizontal = Math.abs(p1.x - p4.x); // Horizontal distance (outer and inner corners)
  return vertical / horizontal;
};

export type FacePosition = 'center' | 'left' | 'right' | 'up' | 'down' | 'unknown';

interface UseFaceDetectionProps {
  videoRef?: RefObject<WebcamRefTypes>;
  shouldPreload?: boolean;
}

export const useFaceDetection = ({
  videoRef,
  shouldPreload = true,
}: UseFaceDetectionProps) => {
  const [facePosition, setFacePosition] = useState<FacePosition>('unknown');
  const detectionFrameId = useRef<number | null>(null);
  const frameSkipCounter = useRef<number>(0);
  const blinkStartTime = useRef<number | null>(null);
  const lastBlinkTime = useRef<number>(0);
  const mockDetectionIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const {
    preload,
    isLoading: isModelLoading,
    detector,
  } = usePreloadLivenessModels({shouldPreload});

  const currentChallenge = useLivenessStore(state => state.currentChallenge);
  const currentChallengeRef = useSyncRef(currentChallenge);
  const isBlinked = useLivenessStore(state => state.isBlinked);
  const isBlinkRef = useSyncRef(isBlinked);
  const setIsBlinked = useLivenessStore(state => state.setIsBlinked);
  const setChallengeProgress = useProgressStore(state => state.setProgress);

  const throttledSetFacePosition = useRef(
    throttle((position: FacePosition) => {
      setFacePosition(position);
    }, FACE_POSITION_THROTTLE),
  ).current;

  const throttledSetChallengeProgress = useRef(
    throttle((progress: number) => {
      setChallengeProgress(progress);
    }, PROGRESS_THROTTLE),
  ).current;

  const throttledProcessBlink = useRef(
    throttle((leftEAR: number, rightEAR: number, isCenter: boolean, now: number) => {
      const areEyesClosed =
        isCenter && leftEAR < EAR_THRESHOLD && rightEAR < EAR_THRESHOLD;

      if (areEyesClosed) {
        if (!blinkStartTime.current) {
          blinkStartTime.current = now;
        }
      } else if (blinkStartTime.current) {
        const blinkDuration = now - blinkStartTime.current;
        if (
          blinkDuration >= 80 &&
          blinkDuration <= 400 &&
          now - lastBlinkTime.current > 1000
        ) {
          setIsBlinked(true);
          lastBlinkTime.current = now;
        }
        blinkStartTime.current = null;
      }
    }, BLINK_CHECK_THROTTLE),
  ).current;

  useEffect(() => {
    if (!shouldPreload) {
      return;
    }

    async function initializeDetection() {
      if (detector) {
        return;
      }

      await preload();
    }

    void initializeDetection();

    return () => {
      if (detectionFrameId.current) {
        cancelAnimationFrame(detectionFrameId.current);
      }
      if (mockDetectionIntervalRef.current) {
        clearInterval(mockDetectionIntervalRef.current);
      }
    };
  }, [detector, preload, shouldPreload]);

  const detectFace = useCallback(async () => {
    const videoElement = videoRef?.current?.video;

    if (!detector || !videoElement || videoElement.readyState !== 4) {
      detectionFrameId.current = requestAnimationFrame(detectFace);
      return;
    }

    // Frame skipping for memory optimization
    frameSkipCounter.current =
      (frameSkipCounter.current + 1) % (DETECTION_FRAME_SKIP + 1);
    if (frameSkipCounter.current !== 0) {
      detectionFrameId.current = requestAnimationFrame(detectFace);
      return;
    }

    try {
      const faces = await detector.estimateFaces(videoElement);

      if (faces.length > 0) {
        const face = faces[0];
        const keypoints = face.keypoints;

        if (keypoints.length < 468) {
          detectionFrameId.current = requestAnimationFrame(detectFace);
          return;
        }

        const now = Date.now();

        // --- Head movement detection ---
        const leftEyeInner = keypoints[133];
        const rightEyeInner = keypoints[362];
        const noseTip = keypoints[1];

        const eyeCenterX = (leftEyeInner.x + rightEyeInner.x) / 2;
        const eyeCenterY = (leftEyeInner.y + rightEyeInner.y) / 2;
        const eyeDistance = Math.sqrt(
          Math.pow(rightEyeInner.x - leftEyeInner.x, 2) +
            Math.pow(rightEyeInner.y - leftEyeInner.y, 2),
        );

        const offsetX = (noseTip.x - eyeCenterX) / eyeDistance;
        const offsetY = (noseTip.y - eyeCenterY) / eyeDistance;

        const isCenter = offsetX > -0.2 && offsetX < 0.5 && offsetY > 0.8;

        if (offsetY < UP_THRESHOLD) {
          throttledSetFacePosition('up');
        } else if (offsetX > LEFT_THRESHOLD) {
          throttledSetFacePosition('left');
        } else if (offsetX < RIGHT_THRESHOLD) {
          throttledSetFacePosition('right');
        } else if (isCenter) {
          throttledSetFacePosition('center');
        } else {
          throttledSetFacePosition('unknown');
        }

        let progress = 0;
        if (currentChallengeRef.current === 'TURN_LEFT') {
          progress = (100 * offsetX) / LEFT_THRESHOLD;
        } else if (currentChallengeRef.current === 'TURN_RIGHT') {
          progress = (100 * offsetX) / RIGHT_THRESHOLD;
        } else if (currentChallengeRef.current === 'TURN_UP') {
          progress =
            (100 * (offsetY - NORMAL_OFFSET_Y)) / (UP_THRESHOLD - NORMAL_OFFSET_Y);
        } else if (currentChallengeRef.current === 'BLINK') {
          progress = isBlinkRef.current ? 100 : 0;
        }
        progress = Math.max(0, Math.min(100, progress));
        throttledSetChallengeProgress(progress);

        // --- Blink detection using EAR ---
        const leftEyeIndices = [33, 159, 145, 133]; // outer, upper, lower, inner
        const rightEyeIndices = [362, 386, 374, 263]; // outer, upper, lower, inner

        const leftEAR = calculateEAR(keypoints, leftEyeIndices);
        const rightEAR = calculateEAR(keypoints, rightEyeIndices);

        throttledProcessBlink(leftEAR, rightEAR, isCenter, now);
      } else {
        throttledSetFacePosition('unknown');
        throttledSetChallengeProgress(0);
      }
    } catch (error) {
      console.error('Face error detection', error);
    }

    detectionFrameId.current = requestAnimationFrame(detectFace);
  }, [
    detector,
    videoRef,
    currentChallengeRef,
    throttledSetChallengeProgress,
    throttledProcessBlink,
    throttledSetFacePosition,
    isBlinkRef,
  ]);

  const startDetection = useCallback(() => {
    if (!shouldPreload) {
      return;
    }

    if (detectionFrameId.current) {
      cancelAnimationFrame(detectionFrameId.current);
    }
    detectionFrameId.current = requestAnimationFrame(detectFace);
  }, [detectFace, shouldPreload]);

  const isCorrectPosition = useCallback(() => {
    if (!currentChallenge || !facePosition) return false;

    switch (currentChallenge) {
      case 'TURN_LEFT':
        return facePosition === 'left';
      case 'TURN_RIGHT':
        return facePosition === 'right';
      case 'TURN_UP':
        return facePosition === 'up';
      case 'BLINK':
        return isBlinked;
      default:
        return false;
    }
  }, [currentChallenge, facePosition, isBlinked]);

  useEffect(() => {
    return () => {
      if (detectionFrameId.current) {
        cancelAnimationFrame(detectionFrameId.current);
        detectionFrameId.current = null;
      }
      if (mockDetectionIntervalRef.current) {
        clearInterval(mockDetectionIntervalRef.current);
        mockDetectionIntervalRef.current = null;
      }
    };
  }, []);

  return {
    isModelLoading,
    facePosition,
    startDetection,
    isCorrectPosition,
  };
};
